import { defineStore } from 'pinia'
import { ref } from 'vue'
import { showToast } from 'vant'
import { t } from '@/i18n'
import { apiLike, apiUnlike, apiLikeList } from '@/apis/theater'
import type { IResLikeListItem } from '@/types/apis/theater'
import { useUserStore } from './user'
import { usePaginatedStore } from '@/composables/useInteractionStore'

export const useLikeStore = defineStore('like', () => {
  const userStore = useUserStore()

  // 点赞状态：仅用 Set 维护 episodeId，O(1) 查询
  // Set 只由 toggleLike 写入，不由 loadList 初始化；加载阶段依赖 feed API 的 IsLiked 字段
  const likedEpisodeIds = ref(new Set<number>())

  // Profile 列表展示的分页数据
  const paginated = usePaginatedStore<IResLikeListItem>({
    storageKey: 'liked-scripts',
    fetchApi: apiLikeList,
    getItemId: item => item.Id,
  })

  const checkIsLiked = (episodeId: number): boolean => likedEpisodeIds.value.has(episodeId)

  /** 切换点赞状态：乐观更新 Set → 动作 API → 刷新分页 Map（Profile 数据源） */
  const toggleLike = async (
    episodeId: number,
    scriptId: number,
    currentLikedState?: boolean,
  ): Promise<boolean> => {
    if (!userStore.isLogin) {
      showToast(t('common.messages.pleaseLogin'))
      return false
    }

    // 乐观更新 Set（仅改 Set，不改 Map）
    const isCurrentlyLiked = currentLikedState ?? checkIsLiked(episodeId)
    isCurrentlyLiked ? likedEpisodeIds.value.delete(episodeId) : likedEpisodeIds.value.add(episodeId)

    try {
      if (isCurrentlyLiked) {
        await apiUnlike({ episodeId, scriptId })
      } else {
        await apiLike({ episodeId, scriptId })
      }
      // 操作成功后仅刷新分页 Map（Profile 数据源），不重建 Set
      // Set 由乐观更新保证正确性，不应被列表 API 返回数据覆盖
      await paginated.loadList(1)
      return true
    } catch {
      // 回滚 Set
      if (isCurrentlyLiked) {
        likedEpisodeIds.value.add(episodeId)
      } else {
        likedEpisodeIds.value.delete(episodeId)
      }
      showToast(t('common.errors.operationFailed'))
      return false
    }
  }

  /** 加载分页列表（仅 Profile 使用，不触碰 Set） */
  const loadList = async (pageNum?: number) => {
    await paginated.loadList(pageNum)
  }

  /** 清除所有数据 */
  const clear = () => {
    likedEpisodeIds.value = new Set()
    paginated.clear()
  }

  return {
    likedEpisodeIds,
    list: paginated.list,
    loading: paginated.loading,
    loaded: paginated.loaded,
    hasMore: paginated.hasMore,
    isLiked: checkIsLiked,
    toggleLike,
    loadList,
    clear,
  }
})
